import React, { useEffect, useState } from 'react';
import { styles } from './BundleOffering.css';
import { BundleOffering as BaseBundleOffering } from '../BundleOfferingsContext';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import { submitTracking } from '@jbhi-fi/components/src/Tracking/utils/shopifyActionTrackingHelper';
import { useMinicartUiContext } from '../../../../utils/cart/context/MinicartUiContext';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformProductGtmDataForTracking } from '../../../../utils/cart/transformProductGtmDataForTracking';
import { trackProductClick } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackProductClick';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { trackViewCartShopify } from '../../../../utils/cart/trackViewCartShopify';
import { useCartContext } from '../../../../utils/cart/context/CartContext';

export interface AddHandler {
  variantId: number;
  quantity: number;
  linkedLineItemUuid: string;
  location: string;
  tagManagerData: ProductGtmData;
  maxQuantity: number;
  productGroup: number;
  availabilityCanPreOrder: boolean;
  availabilityFulfilment: string;
  availabilityProductLifecycle: string;
  freightPrice: number;
  sku: number;
  itemListName: string;
}

export type OfferingProps = BaseBundleOffering & {
  ctaLabel: string;
  onClick: (args: AddHandler) => void;
};

const Offering = ({
  heading,
  description,
  imageUrl,
  price,
  wasPrice,
  ctaLabel,
  onClick,
  productHandle,
  variantId,
  tagManagerData,
  location,
  linkedLineItemUuid,
  sku,
  productGroup,
  maxQuantity,
  freightPrice,
  availabilityCanPreOrder,
  availabilityFulfilment,
  availabilityProductLifecycle,
}: OfferingProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const isCartPage = window.location.pathname === '/cart';
  const page = isCartPage ? 'cart' : 'minicart';
  const itemListName = `recommendations-${page}-GIAT`;
  const productHref = `/products/${productHandle}?variant=${variantId}`;
  const { isOpen: isMinicartOpen } = useMinicartUiContext();
  const [ctaState, setCtaState] = useState(CartActionState.Default);
  const { cart } = useCartContext();

  const trackGtmButtonImpression = () => {
    submitTracking({
      event: 'productImpression',
      trackingDescriptor: {
        page: page,
        group: 'shopifyBundle',
        category: 'getItAllTogether',
        currency: getStoreCurrency(),
        ecommerceExtraProps: {
          ...tagManagerData,
          list: itemListName,
        },
      },
      gtm: {},
    });
    trackViewItemList({
      currency: getStoreCurrency(),
      listName: itemListName,
      viewedItems: [transformProductGtmDataForTracking(tagManagerData, page, variantId)],
    });
  };

  useEffect(() => {
    if (page === 'minicart' && isMinicartOpen) {
      trackGtmButtonImpression();
    }
    if (page === 'cart' && !isMinicartOpen) {
      trackGtmButtonImpression();
    }
  }, [isMinicartOpen]);

  const handleAddToCart = () => {
    trackAddToCart({
      location: page,
      currency: getStoreCurrency(),
      addedItems: transformProductGtmDataForTracking(tagManagerData, itemListName, variantId),
    });
    trackViewCartShopify(cart, page);
    submitTracking({
      event: 'addToCart',
      trackingDescriptor: {
        page: page,
        group: 'shopifyBundle',
        category: 'getItAllTogether',
        currency: getStoreCurrency(),
        ecommerceExtraProps: {
          ...tagManagerData,
          list: itemListName,
        },
      },
      gtm: {},
    });
    if (onClick && !isLoading) {
      setIsLoading(true);
      onClick({
        variantId,
        quantity: 1,
        linkedLineItemUuid,
        location,
        tagManagerData,
        productGroup,
        maxQuantity,
        availabilityCanPreOrder,
        availabilityFulfilment,
        availabilityProductLifecycle,
        freightPrice,
        sku: Number(sku),
        itemListName,
      });
    }
    setCtaState(CartActionState.Complete);
  };
  const handleLinkClick = () => {
    submitTracking({
      event: 'productClick',
      trackingDescriptor: {
        page: page,
        group: 'shopifyBundle',
        category: 'getItAllTogether',
        currency: getStoreCurrency(),
        ecommerceExtraProps: {
          ...tagManagerData,
          list: itemListName,
        },
      },
      gtm: {},
    });
    trackProductClick({
      location: page,
      currency: getStoreCurrency(),
      clickedProduct: transformProductGtmDataForTracking(tagManagerData, page),
    });
  };

  return (
    <div className={styles.item}>
      <div className={styles.content}>
        <a className={styles.image} href={productHref} onClick={handleLinkClick}>
          <img src={imageUrl} alt={heading} loading="lazy" />
        </a>
        <a className={styles.heading} href={productHref} onClick={handleLinkClick}>
          <p>{heading}</p>
        </a>
        {description ? <span>{description}</span> : null}
        <div className={styles.price}>
          <span>{price}</span>
        </div>
      </div>
      <ActionButton
        label={ctaLabel}
        onClick={handleAddToCart}
        isLoading={isLoading}
        state={ctaState}
        actionFinishedLabel="Added!"
      />
    </div>
  );
};

export type BundleOfferingProps = {
  title: string;
  ctaLabel: string;
  offerings: Array<BaseBundleOffering>;
  onAdd: (args: AddHandler) => void;
};

export const BundleOffering = ({ title, ctaLabel, offerings, onAdd }: BundleOfferingProps): JSX.Element => {
  return (
    <div className={styles.container}>
      <DomFont className={styles.title}>{title}</DomFont>
      {offerings.map((offering, i) => (
        <Offering key={i} {...offering} ctaLabel={ctaLabel} onClick={onAdd} />
      ))}
    </div>
  );
};
